/**
 * Wide Stage Layouts — handcrafted sand-only terrain for 4096px world width.
 *
 * All stages are pure sand (ground segments only). No wireframe platforms,
 * nodes, or beams. Everything is destructible.
 *
 * Design rules:
 * - Main ground surfaceY ≥ 900 (1300px clearance to upper blast zone at -400)
 * - Elevated islands surfaceY ≥ 700 (1100px clearance — no free top KOs)
 * - Ground depth 3-6 for standard terrain, 8-14 for massive structures
 * - Sand draping adds ~70px above and ~40px below each segment
 * - Edge taper (12 cells) creates natural slopes at segment boundaries
 * - Adjacent segments with small height differences merge into rolling terrain
 * - Separated segments create distinct islands over void
 */

import type { WireframeLayout, WireframeGround } from '../types';
import { ARCHETYPE } from '../constants';

const D = ARCHETYPE.GROUND_DEPTH;

/** Helper — all wide layouts return sand-only (no wireframes). */
function sandOnly(groundSegments: WireframeGround[]): WireframeLayout {
  return { nodes: [], beams: [], platforms: [], groundSegments };
}

// ── Final Destination — "The Proving Ground" ──────────────────────
//
// Single continuous landmass spanning the full width. Gentle sine-wave
// undulation. No islands, no platforms, no elevation tricks. Pure aim.
// The flattest, fairest stage in the game.

export function generateFinalDestinationLayout(
  stageLeft: number,
  stageWidth: number,
  _playerCount: number,
): WireframeLayout {
  // Single wide ground — sand draping adds natural rolling hills
  return sandOnly([{
    x: stageLeft + 100,
    width: stageWidth - 200,
    surfaceY: 950,
    depth: D + 2,
  }]);
}

// ── Battlefield — "Sacred Ground" ─────────────────────────────────
//
// Wide main ground with 3 elevated sand islands in triangular formation.
// The Smash standard adapted for artillery: high ground matters for
// angle advantage, but each island is destructible.

export function generateBattlefieldLayout(
  stageLeft: number,
  stageWidth: number,
  _playerCount: number,
): WireframeLayout {
  const cx = stageLeft + stageWidth / 2;
  const groundSegments: WireframeGround[] = [];

  // Main ground — wide, low
  groundSegments.push({
    x: stageLeft + 80,
    width: stageWidth - 160,
    surfaceY: 960,
    depth: D + 2,
  });

  // Left island — elevated
  groundSegments.push({
    x: cx - 1200,
    width: 500,
    surfaceY: 750,
    depth: D + 4,
  });

  // Center island — highest, narrower
  groundSegments.push({
    x: cx - 220,
    width: 440,
    surfaceY: 700,
    depth: D + 3,
  });

  // Right island — elevated
  groundSegments.push({
    x: cx + 700,
    width: 500,
    surfaceY: 750,
    depth: D + 4,
  });

  return sandOnly(groundSegments);
}

// ── Fountain — "Rising Waters" ────────────────────────────────────
//
// Central mountain with gradually sloping terrain on both sides.
// 2 flanking plateaus at medium height. The center peak rewards
// positioning but is exposed to shots from below.

export function generateFountainLayout(
  stageLeft: number,
  stageWidth: number,
  _playerCount: number,
): WireframeLayout {
  const cx = stageLeft + stageWidth / 2;
  const groundSegments: WireframeGround[] = [];

  // Left lowlands
  groundSegments.push({
    x: stageLeft + 100,
    width: 900,
    surfaceY: 980,
    depth: D + 2,
  });

  // Left plateau — stepping stone to center
  groundSegments.push({
    x: stageLeft + 1050,
    width: 500,
    surfaceY: 900,
    depth: D + 3,
  });

  // Central mountain — the high ground
  groundSegments.push({
    x: cx - 350,
    width: 700,
    surfaceY: 800,
    depth: D + 6,
  });

  // Right plateau — stepping stone from center
  groundSegments.push({
    x: cx + 400,
    width: 500,
    surfaceY: 900,
    depth: D + 3,
  });

  // Right lowlands
  groundSegments.push({
    x: stageLeft + stageWidth - 1000,
    width: 900,
    surfaceY: 980,
    depth: D + 2,
  });

  return sandOnly(groundSegments);
}

// ── Stadium — "The Arena" ─────────────────────────────────────────
//
// Wide flat main ground with 2 symmetric elevated side platforms.
// Clean, competitive. The side platforms offer angle advantage
// but are smaller and more exposed to cratering.

export function generateStadiumLayout(
  stageLeft: number,
  stageWidth: number,
  _playerCount: number,
): WireframeLayout {
  const cx = stageLeft + stageWidth / 2;
  const groundSegments: WireframeGround[] = [];

  // Main ground — wide center
  groundSegments.push({
    x: stageLeft + 200,
    width: stageWidth - 400,
    surfaceY: 950,
    depth: D + 2,
  });

  // Left elevated wing
  groundSegments.push({
    x: stageLeft + 100,
    width: 600,
    surfaceY: 800,
    depth: D + 5,
  });

  // Right elevated wing
  groundSegments.push({
    x: stageLeft + stageWidth - 700,
    width: 600,
    surfaceY: 800,
    depth: D + 5,
  });

  return sandOnly(groundSegments);
}

// ── Skybridge — "The Crossing" ────────────────────────────────────
//
// 3 major islands at different heights connected by thin sand bridges.
// The bridges are destructible — blow them out to strand your opponent.
// Spans the full width with void gaps between islands.

export function generateSkybridgeLayout(
  stageLeft: number,
  stageWidth: number,
  _playerCount: number,
): WireframeLayout {
  const cx = stageLeft + stageWidth / 2;
  const groundSegments: WireframeGround[] = [];

  // Left island — mid height
  groundSegments.push({
    x: stageLeft + 100,
    width: 800,
    surfaceY: 900,
    depth: D + 5,
  });

  // Left-to-center bridge — thin, destructible
  groundSegments.push({
    x: stageLeft + 950,
    width: 350,
    surfaceY: 850,
    depth: D,
  });

  // Center island — elevated, dominant
  groundSegments.push({
    x: cx - 450,
    width: 900,
    surfaceY: 800,
    depth: D + 6,
  });

  // Center-to-right bridge — thin, destructible
  groundSegments.push({
    x: cx + 500,
    width: 350,
    surfaceY: 850,
    depth: D,
  });

  // Right island — mid height
  groundSegments.push({
    x: stageLeft + stageWidth - 900,
    width: 800,
    surfaceY: 900,
    depth: D + 5,
  });

  return sandOnly(groundSegments);
}

// ── Twin Peaks — "The Divide" ─────────────────────────────────────
//
// Two massive sand pillars separated by a wide chasm. A small
// sand island sits deep in the gap as a rescue ledge.
// Each pillar is thick enough to survive serious cratering.

export function generateTwinPeaksLayout(
  stageLeft: number,
  stageWidth: number,
  _playerCount: number,
): WireframeLayout {
  const cx = stageLeft + stageWidth / 2;
  const groundSegments: WireframeGround[] = [];

  // Left pillar — massive
  groundSegments.push({
    x: stageLeft + 100,
    width: 1200,
    surfaceY: 800,
    depth: D + 8,
  });

  // Rescue island — small, deep in the chasm
  groundSegments.push({
    x: cx - 200,
    width: 400,
    surfaceY: 1050,
    depth: D + 2,
  });

  // Right pillar — massive
  groundSegments.push({
    x: stageLeft + stageWidth - 1300,
    width: 1200,
    surfaceY: 800,
    depth: D + 8,
  });

  return sandOnly(groundSegments);
}

// ── Colosseum — "Arena of Ruin" ───────────────────────────────────
//
// Wide bowl: high walls on left and right slope down to a low center
// floor. Tanks in the bowl are protected from side KOs but exposed
// to plunging fire from the walls. All sand, all destructible.

export function generateColosseumLayout(
  stageLeft: number,
  stageWidth: number,
  _playerCount: number,
): WireframeLayout {
  const cx = stageLeft + stageWidth / 2;
  const groundSegments: WireframeGround[] = [];

  // Left wall — high, thick
  groundSegments.push({
    x: stageLeft + 100,
    width: 600,
    surfaceY: 750,
    depth: D + 8,
  });

  // Left slope
  groundSegments.push({
    x: stageLeft + 700,
    width: 500,
    surfaceY: 850,
    depth: D + 5,
  });

  // Center floor — low, wide
  groundSegments.push({
    x: cx - 500,
    width: 1000,
    surfaceY: 980,
    depth: D + 3,
  });

  // Right slope
  groundSegments.push({
    x: cx + 500,
    width: 500,
    surfaceY: 850,
    depth: D + 5,
  });

  // Right wall — high, thick
  groundSegments.push({
    x: stageLeft + stageWidth - 700,
    width: 600,
    surfaceY: 750,
    depth: D + 8,
  });

  return sandOnly(groundSegments);
}

// ── Archipelago — "Scattered Isles" ───────────────────────────────
//
// 6 disconnected sand islands over void at varied heights and widths.
// No bridges — you have to use knockback and grapple to traverse.
// Each island is small enough that 2-3 craters can destroy it.

export function generateArchipelagoLayout(
  stageLeft: number,
  stageWidth: number,
  _playerCount: number,
): WireframeLayout {
  const cx = stageLeft + stageWidth / 2;
  const groundSegments: WireframeGround[] = [];

  // Island 1 — far left, mid height
  groundSegments.push({
    x: stageLeft + 80,
    width: 450,
    surfaceY: 900,
    depth: D + 4,
  });

  // Island 2 — left-center, higher
  groundSegments.push({
    x: stageLeft + 700,
    width: 500,
    surfaceY: 820,
    depth: D + 5,
  });

  // Island 3 — center, low (main fighting area)
  groundSegments.push({
    x: cx - 300,
    width: 600,
    surfaceY: 960,
    depth: D + 4,
  });

  // Island 4 — right-center, higher
  groundSegments.push({
    x: cx + 500,
    width: 500,
    surfaceY: 820,
    depth: D + 5,
  });

  // Island 5 — far right, mid height
  groundSegments.push({
    x: stageLeft + stageWidth - 530,
    width: 450,
    surfaceY: 900,
    depth: D + 4,
  });

  // Island 6 — small floating chunk, high
  groundSegments.push({
    x: cx - 150,
    width: 300,
    surfaceY: 720,
    depth: D + 3,
  });

  return sandOnly(groundSegments);
}

// ── The Pit — "No Escape" ─────────────────────────────────────────
//
// Massive terrain mass with deep center depression. Thick sand walls
// on both sides create a natural pit. Fighting happens in the crater.
// Terrain so thick that explosions carve tunnels and caves over time.

export function generateThePitLayout(
  stageLeft: number,
  stageWidth: number,
  _playerCount: number,
): WireframeLayout {
  const cx = stageLeft + stageWidth / 2;
  const groundSegments: WireframeGround[] = [];

  // Left wall — very thick
  groundSegments.push({
    x: stageLeft + 100,
    width: 800,
    surfaceY: 750,
    depth: D + 12,
  });

  // Left inner slope
  groundSegments.push({
    x: stageLeft + 900,
    width: 400,
    surfaceY: 850,
    depth: D + 8,
  });

  // Pit floor — low, wide
  groundSegments.push({
    x: cx - 500,
    width: 1000,
    surfaceY: 1000,
    depth: D + 4,
  });

  // Right inner slope
  groundSegments.push({
    x: cx + 500,
    width: 400,
    surfaceY: 850,
    depth: D + 8,
  });

  // Right wall — very thick
  groundSegments.push({
    x: stageLeft + stageWidth - 900,
    width: 800,
    surfaceY: 750,
    depth: D + 12,
  });

  return sandOnly(groundSegments);
}

// ── Caldera — "Ring of Fire" ──────────────────────────────────────
//
// Volcanic crater: raised rim around a depressed center. The rim
// is thin and destructible — blow holes in it to create sight lines
// into the crater. Center floor is low and exposed.

export function generateCalderaLayout(
  stageLeft: number,
  stageWidth: number,
  _playerCount: number,
): WireframeLayout {
  const cx = stageLeft + stageWidth / 2;
  const groundSegments: WireframeGround[] = [];

  // Far left lowlands (outside the rim)
  groundSegments.push({
    x: stageLeft + 80,
    width: 500,
    surfaceY: 950,
    depth: D + 3,
  });

  // Left rim — elevated, thin
  groundSegments.push({
    x: stageLeft + 650,
    width: 400,
    surfaceY: 780,
    depth: D + 4,
  });

  // Left inner slope
  groundSegments.push({
    x: stageLeft + 1050,
    width: 350,
    surfaceY: 880,
    depth: D + 3,
  });

  // Crater floor — low, wide
  groundSegments.push({
    x: cx - 450,
    width: 900,
    surfaceY: 980,
    depth: D + 3,
  });

  // Right inner slope
  groundSegments.push({
    x: cx + 500,
    width: 350,
    surfaceY: 880,
    depth: D + 3,
  });

  // Right rim — elevated, thin
  groundSegments.push({
    x: stageLeft + stageWidth - 1050,
    width: 400,
    surfaceY: 780,
    depth: D + 4,
  });

  // Far right lowlands (outside the rim)
  groundSegments.push({
    x: stageLeft + stageWidth - 580,
    width: 500,
    surfaceY: 950,
    depth: D + 3,
  });

  return sandOnly(groundSegments);
}

// ── Stalactite — "Frostbite Cavern" ──────────────────────────────
//
// Ceiling masses hanging from the top + narrow ground floor below.
// The hanging terrain blocks vertical shots and creates sheltered zones.
// All sand — ceiling masses are destructible. Tall stage (1800px).

export function generateStalactiteLayout(
  stageLeft: number,
  stageWidth: number,
  _playerCount: number,
): WireframeLayout {
  const cx = stageLeft + stageWidth / 2;
  const groundSegments: WireframeGround[] = [];

  // Ground floor — full width, low
  groundSegments.push({
    x: stageLeft + 80,
    width: stageWidth - 160,
    surfaceY: 1050,
    depth: D + 3,
  });

  // Ceiling masses — sand segments placed high with deep depth
  // so sand draping creates thick hanging formations

  // Hang 1 — left
  groundSegments.push({
    x: stageLeft + 200,
    width: 500,
    surfaceY: 200,
    depth: D + 14,
  });

  // Hang 2 — left-center
  groundSegments.push({
    x: stageLeft + 850,
    width: 450,
    surfaceY: 150,
    depth: D + 18,
  });

  // Hang 3 — center (longest)
  groundSegments.push({
    x: cx - 250,
    width: 500,
    surfaceY: 120,
    depth: D + 22,
  });

  // Hang 4 — right-center
  groundSegments.push({
    x: cx + 400,
    width: 450,
    surfaceY: 160,
    depth: D + 16,
  });

  // Hang 5 — right
  groundSegments.push({
    x: stageLeft + stageWidth - 700,
    width: 500,
    surfaceY: 190,
    depth: D + 14,
  });

  // Mid-height island — refuge between ceiling and floor
  groundSegments.push({
    x: cx - 300,
    width: 600,
    surfaceY: 750,
    depth: D + 4,
  });

  return sandOnly(groundSegments);
}
